import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { API_URL } from '../lib/utils';
import { toast } from 'sonner';
import { ShieldCheck, Clock, AlertTriangle, Loader2, BadgeCheck, BellRing } from 'lucide-react';

const VISUAL = {
  green: { cls: 'bg-emerald-100 text-emerald-700', icon: ShieldCheck, label: 'Verified by INASW' },
  yellow: { cls: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Renewal due soon' },
  gray: { cls: 'bg-gray-100 text-gray-600', icon: Clock, label: 'Pending verification' },
  red: { cls: 'bg-red-100 text-red-700', icon: AlertTriangle, label: 'Not verified' },
};

const JobSeekerLicenseVerification = () => {
  const [mine, setMine] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  const [form, setForm] = useState({
    legal_first_name: '', legal_last_name: '', previous_name: '', state: '', license_type: '',
    license_number: '', expiration_date: '',
  });

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/license/mine`);
      const v = res.data.verification;
      setMine(v);
      if (v) {
        setForm({
          legal_first_name: v.legal_first_name || '', legal_last_name: v.legal_last_name || '',
          previous_name: v.previous_name || '',
          state: v.state || '', license_type: v.license_type || '',
          license_number: v.license_number || '', expiration_date: (v.expiration_date || '').slice(0, 10),
        });
      }
    } catch { /* ignore */ } finally { setLoaded(true); }
    try {
      const a = await axios.get(`${API_URL}/job-alerts`);
      setAlertsEnabled(a.data.enabled);
    } catch { /* ignore */ }
  };

  const toggleAlerts = async (enabled) => {
    setAlertsEnabled(enabled);
    try {
      await axios.put(`${API_URL}/job-alerts`, { enabled });
      toast.success(enabled ? 'Job alerts on — we\'ll email new matching jobs' : 'Job alerts turned off');
    } catch {
      setAlertsEnabled(!enabled);
      toast.error('Could not update job alerts');
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API_URL}/license/submit`, form);
      toast.success('License info submitted for verification');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  const cfg = mine ? (VISUAL[mine.display?.visual_status] || VISUAL.gray) : VISUAL.gray;
  const Icon = cfg.icon;

  return (
    <>
    <div className="bg-white rounded-xl p-6 border border-gray-100 mb-6" data-testid="job-alerts-section">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
            <BellRing className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Job Alerts</h3>
            <p className="text-sm text-gray-500">Get an email when new jobs matching your {mine?.license_type || 'license'} and location are posted.</p>
          </div>
        </div>
        <Switch checked={alertsEnabled} onCheckedChange={toggleAlerts} data-testid="job-alerts-toggle" />
      </div>
    </div>

    <div className="bg-white rounded-xl p-6 border border-gray-100" data-testid="license-verification-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <BadgeCheck className="w-5 h-5 text-emerald-600" />
          <h3 className="font-semibold text-gray-900">License Verification</h3>
        </div>
        {mine && (
          <Badge className={`${cfg.cls} text-xs`}><Icon className="w-3 h-3 mr-1" />{cfg.label}</Badge>
        )}
      </div>

      {mine?.display?.public_verified ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4 text-sm text-emerald-800">
          Your {mine.license_type} license is verified and shows a green "License Verified by INASW" badge to employers. Submit updated info below if your license changes — only an admin can re-verify.
        </div>
      ) : (
        <p className="text-sm text-gray-500 mb-4">
          Submit your license details and our team will verify them against your state board. You cannot mark your own license verified.
        </p>
      )}

      {mine?.correction_requested && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 mb-4" data-testid="lv-correction-alert">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Please review your license information</p>
              <p className="mt-1">{mine.correction_message || 'We were unable to verify your license. Please review your details below and resubmit.'}</p>
              <p className="mt-1 text-amber-800">Update the fields below and click Update — your record will return to our review queue.</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
        <p className="sm:col-span-2 text-xs text-gray-500 -mb-1">Please enter your name exactly as it appears on your professional license. INASW uses this to verify your credentials through official state licensing records. Do not enter your Social Security number.</p>
        <div>
          <Label className="text-sm">Legal First Name</Label>
          <Input value={form.legal_first_name} onChange={(e) => setForm({ ...form, legal_first_name: e.target.value })} className="mt-1" data-testid="lv-first-input" required />
        </div>
        <div>
          <Label className="text-sm">Legal Last Name</Label>
          <Input value={form.legal_last_name} onChange={(e) => setForm({ ...form, legal_last_name: e.target.value })} className="mt-1" data-testid="lv-last-input" required />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-sm">Previous, Maiden, or Other Name on Your License <span className="text-gray-400 font-normal">(optional)</span></Label>
          <Input value={form.previous_name} onChange={(e) => setForm({ ...form, previous_name: e.target.value })} className="mt-1" data-testid="lv-previous-input" placeholder="If your license is under a different name" />
        </div>
        <div>
          <Label className="text-sm">State</Label>
          <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="TX" className="mt-1" data-testid="lv-state-input" required />
        </div>
        <div>
          <Label className="text-sm">License Type</Label>
          <Select value={form.license_type} onValueChange={(v) => setForm({ ...form, license_type: v })}>
            <SelectTrigger className="mt-1" data-testid="lv-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {['LCSW', 'LMSW', 'LBSW', 'SWA'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">License Number</Label>
          <Input value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} className="mt-1" data-testid="lv-number-input" required />
        </div>
        <div>
          <Label className="text-sm">Expiration Date</Label>
          <Input type="date" value={form.expiration_date} onChange={(e) => setForm({ ...form, expiration_date: e.target.value })} className="mt-1" data-testid="lv-exp-input" />
        </div>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="lv-submit-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BadgeCheck className="w-4 h-4 mr-2" />}
            {mine ? 'Update License Info' : 'Submit for Verification'}
          </Button>
        </div>
      </form>
    </div>
    </>
  );
};

export default JobSeekerLicenseVerification;
